import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, Trash2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { api, formatApiError } from "../../lib/api";
import { uploadFile } from "../../lib/field";

const B = process.env.REACT_APP_BACKEND_URL;
export const MGR_STATUS = { BARU: "Baru", DITINJAU: "Ditinjau", WAWANCARA: "Wawancara / Verifikasi Kompetensi", PERLU_PERBAIKAN: "Perlu Perbaikan", TERVERIFIKASI: "Terverifikasi", DITOLAK: "Ditolak", DINONAKTIFKAN: "Dinonaktifkan" };
export const COFFEE = { ROBUSTA: "Robusta", ARABIKA: "Arabika", LIBERIKA: "Liberika", LAINNYA: "Lainnya" };
export const SKILLS = { fromScratch: "Pembangunan kebun dari lahan kosong", nursery: "Pembibitan", planting: "Penanaman", fertilizing: "Pemupukan", replanting: "Penyulaman", pruning: "Pemangkasan", shadeTrees: "Tanaman penaung", pestDisease: "Hama & penyakit", harvest: "Panen", postHarvest: "Pascapanen", qualityControl: "Quality control", labor: "Pengelolaan tenaga kerja", budgeting: "Penyusunan RAB/biaya", fieldReporting: "Dokumentasi laporan lapangan" };
export const FILE_KINDS = { PROFILE_PHOTO: "Foto profil", CV: "CV", CERTIFICATE: "Sertifikat/pelatihan", EXPERIENCE_PHOTO: "Foto pengalaman kebun", REFERENCE_LETTER: "Surat referensi" };
const EMPTY = { fullName: "", nik: "", birthPlace: "", birthDate: "", address: "", regency: "", province: "Sumatera Barat", phone: "", whatsapp: "", email: "",
  yearsExperience: "", coffeeTypes: [], workRegion: "", largestFarmHa: "", projectsManaged: "", skills: [], skillNotes: "",
  maxHectares: "", maxActiveProjects: "", hasTeam: false, teamSize: "", reachRegions: "", referenceContacts: "", profileSummary: "", motivation: "" };
const NUMS = ["yearsExperience", "largestFarmHa", "projectsManaged", "maxHectares", "maxActiveProjects", "teamSize"];

export const MgrStatusBadge = ({ s }) => {
  const c = { BARU: "bg-sky-100 text-sky-800", DITINJAU: "bg-amber-100 text-amber-800", WAWANCARA: "bg-violet-100 text-violet-800", PERLU_PERBAIKAN: "bg-orange-100 text-orange-800", TERVERIFIKASI: "bg-emerald-100 text-emerald-800", DITOLAK: "bg-red-100 text-red-800", DINONAKTIFKAN: "bg-slate-200 text-slate-700" }[s];
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${c}`} data-testid="manager-status">{MGR_STATUS[s] || s}</span>;
};

const Box = ({ title, children }) => <section className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6 space-y-4"><h2 className="font-bold text-slate-900">{title}</h2>{children}</section>;
const F = ({ label, children }) => <label className="block text-sm"><span className="font-semibold text-slate-700">{label}</span><div className="mt-1">{children}</div></label>;

const Checks = ({ map, value, onChange, prefix }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {Object.entries(map).map(([k, l]) => (
      <label key={k} className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${value.includes(k) ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}>
        <input type="checkbox" checked={value.includes(k)} onChange={() => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k])} data-testid={`${prefix}-${k}`} />{l}
      </label>))}
  </div>
);

const PendingFiles = ({ files, setFiles }) => (
  <div className="space-y-2">
    <div className="flex flex-wrap gap-2">
      {Object.entries(FILE_KINDS).map(([k, l]) => (
        <label key={k} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 border border-emerald-200 rounded-md px-3 py-2 cursor-pointer hover:bg-emerald-50">
          <Upload className="h-4 w-4" />{l}
          <input type="file" multiple={k !== "PROFILE_PHOTO"} className="hidden" accept={["PROFILE_PHOTO", "EXPERIENCE_PHOTO"].includes(k) ? "image/jpeg,image/png,image/webp" : "application/pdf,image/jpeg,image/png,image/webp"}
            onClick={(e) => { e.currentTarget.value = ""; }} onChange={(e) => { const picked = [...e.target.files].map((file) => ({ file, kind: k })); setFiles((s) => [...s, ...picked]); }} data-testid={`mgr-file-input-${k.toLowerCase()}`} />
        </label>))}
    </div>
    <ul className="text-sm space-y-1" data-testid="mgr-pending-files">
      {files.map((f, i) => <li key={i} className="flex justify-between gap-2"><span className="truncate">{FILE_KINDS[f.kind]}: {f.file.name}</span>
        <button type="button" className="text-red-700 text-xs font-semibold" onClick={() => setFiles((s) => s.filter((_, j) => j !== i))}>Hapus</button></li>)}
    </ul>
    <p className="text-xs text-slate-500">Maks 15 MB per file. Dokumen hanya dapat dilihat Anda dan PT SJI.</p>
  </div>
);

export function ManagerForm({ initial, onSubmit, busy, submitLabel }) {
  const [f, setF] = useState(() => {
    const base = { ...EMPTY, ...(initial || {}) };
    NUMS.forEach((k) => { base[k] = base[k] ?? ""; });
    return base;
  });
  const [files, setFiles] = useState([]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const inp = (k, label, props = {}) => <F label={label}><Input value={f[k]} onChange={set(k)} data-testid={`mgr-${k}`} {...props} /></F>;
  const num = (k, label, props = {}) => inp(k, label, { type: "number", min: "0", inputMode: "decimal", ...props });
  const txt = (k, label, req) => <F label={label}><Textarea rows={3} required={req} value={f[k]} onChange={set(k)} data-testid={`mgr-${k}`} /></F>;
  const submit = (e) => {
    e.preventDefault();
    if (!f.coffeeTypes.length) return toast.error("Pilih minimal satu jenis kopi yang dikuasai");
    const body = { ...f };
    NUMS.forEach((k) => { body[k] = body[k] === "" ? 0 : Number(body[k]); });
    ["id", "number", "status", "files", "history", "revisionNote", "rejectionNote", "deactivationNote", "createdAt", "updatedAt", "revisedAt", "responsibility", "rating", "verifiedAt", "verifiedBy"].forEach((k) => delete body[k]);
    return onSubmit(body, files, () => setFiles([]));
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="manager-form">
      <Box title="1. Identitas">
        <div className="grid sm:grid-cols-2 gap-4">
          {inp("fullName", "Nama lengkap *", { required: true })}{inp("nik", initial?.nikMasked ? `NIK tersimpan terenkripsi (${initial.nikMasked}) - isi hanya jika ingin mengganti` : "NIK / nomor identitas *", { required: !initial?.nikMasked, inputMode: "numeric" })}
          {inp("birthPlace", "Tempat lahir *", { required: true })}{inp("birthDate", "Tanggal lahir *", { required: true, type: "date" })}
          {inp("regency", "Kabupaten/Kota *", { required: true })}{inp("province", "Provinsi *", { required: true })}
          {inp("phone", "Nomor HP *", { required: true, inputMode: "tel" })}{inp("whatsapp", "WhatsApp", { inputMode: "tel" })}
          {inp("email", "Email * (dipakai untuk login setelah terverifikasi)", { required: true, type: "email" })}
        </div>
        {txt("address", "Alamat *", true)}
      </Box>
      <Box title="2. Pengalaman">
        <div className="grid sm:grid-cols-2 gap-4">
          {num("yearsExperience", "Lama pengalaman di perkebunan kopi (tahun) *", { required: true, step: "0.5" })}
          {inp("workRegion", "Wilayah kerja * (mis. Sumatera Barat, Tanah Datar)", { required: true })}
          {num("largestFarmHa", "Luas kebun terbesar yang pernah dikelola (ha)", { step: "0.01" })}
          {num("projectsManaged", "Jumlah kebun yang pernah dikelola", { step: "1" })}
        </div>
        <p className="text-sm font-semibold text-slate-700">Jenis kopi yang dikuasai *</p>
        <Checks map={COFFEE} value={f.coffeeTypes} onChange={(v) => setF((s) => ({ ...s, coffeeTypes: v }))} prefix="mgr-coffee" />
        <p className="text-sm font-semibold text-slate-700">Pengalaman / keahlian</p>
        <Checks map={SKILLS} value={f.skills} onChange={(v) => setF((s) => ({ ...s, skills: v }))} prefix="mgr-skill" />
        {txt("skillNotes", "Penjelasan pengalaman (opsional)")}
      </Box>
      <Box title="3. Kapasitas Kerja">
        <div className="grid sm:grid-cols-2 gap-4">
          {num("maxHectares", "Maksimum luas kebun yang sanggup dikelola (ha) *", { required: true, step: "0.01", min: "0.01" })}
          {num("maxActiveProjects", "Maksimum jumlah kebun aktif *", { required: true, step: "1", min: "1" })}
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mt-6"><input type="checkbox" checked={f.hasTeam} onChange={(e) => setF((s) => ({ ...s, hasTeam: e.target.checked }))} data-testid="mgr-hasTeam" />Memiliki tim lapangan</label>
          {num("teamSize", "Estimasi tenaga kerja yang bisa dikoordinasikan", { step: "1" })}
        </div>
        {inp("reachRegions", "Wilayah yang sanggup dijangkau")}
      </Box>
      <Box title="4. Dokumen">
        {initial?.files?.length > 0 && <p className="text-xs text-slate-600">{initial.files.length} file sudah terunggah (lihat di atas).</p>}
        <PendingFiles files={files} setFiles={setFiles} />
        {txt("referenceContacts", "Kontak referensi kerja (nama, hubungan, nomor)")}
      </Box>
      <Box title="5. Catatan">
        {txt("profileSummary", "Profil singkat *", true)}{txt("motivation", "Alasan ingin menjadi Pengelola Perorangan di Salimbado Farm *", true)}
      </Box>
      <p className="text-xs text-slate-500">Pengelola Perorangan bertanggung jawab atas pelaksanaan operasional kebun yang menjadi tanggung jawabnya sesuai kontrak dan standar kebun. Pendaftaran tidak langsung aktif: PT SJI meninjau, dapat mewawancarai/menguji kompetensi, lalu memverifikasi.</p>
      <Button type="submit" disabled={busy} className="w-full sm:w-auto bg-emerald-800 hover:bg-emerald-900" data-testid="mgr-submit-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{submitLabel}</Button>
    </form>
  );
}

const uploadAll = async (id, t, files) => {
  let failed = 0;
  for (const { file, kind } of files) {
    try { await uploadFile(`/public/individual-managers/${id}/files?t=${encodeURIComponent(t)}`, file, { kind }); } catch (e) { failed += 1; toast.error(`${file.name}: ${formatApiError(e)}`); }
  }
  return failed;
};

export function ManagerRegistrationPage() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (body, files, clear) => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/individual-managers", body);
      const failed = await uploadAll(data.id, data.token, files);
      clear(); setDone({ ...data, failed }); window.scrollTo(0, 0);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (done) {
    const link = `/pendaftaran-pengelola/${done.id}?t=${done.token}`;
    return (
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="manager-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-700" />
        <h1 className="text-3xl font-extrabold text-slate-900 mt-4">Pendaftaran berhasil dikirim</h1>
        <p className="mt-2 text-slate-700">Nomor: <b data-testid="manager-number">{done.number}</b> · Status: <MgrStatusBadge s="BARU" /></p>
        {done.failed > 0 && <p className="mt-2 text-sm text-red-700">{done.failed} file gagal diunggah. Unggah ulang lewat tautan di bawah.</p>}
        <p className="mt-4 text-sm text-slate-600">Simpan tautan berikut untuk memantau status dan memperbaiki data bila diminta. Tautan juga dikirim ke email Anda. Jangan bagikan ke orang lain.</p>
        <Link to={link} className="mt-2 block break-all rounded-lg bg-slate-100 p-3 text-sm font-mono text-emerald-800" data-testid="manager-track-link">{window.location.origin}{link}</Link>
      </div>
    );
  }
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-5">
      <div><p className="text-xs font-bold tracking-widest text-emerald-700">PENGELOLA PERORANGAN</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="manager-title">DAFTAR MENJADI PENGELOLA KEBUN KOPI</h1>
        <p className="mt-2 text-slate-600">Untuk profesional berpengalaman yang siap menjadi penanggung jawab operasional kebun kopi.</p></div>
      <ManagerForm onSubmit={submit} busy={busy} submitLabel="Kirim Pendaftaran" />
    </div>
  );
}

export function TrackManagerPage() {
  const { id } = useParams();
  const t = useSearchParams()[0].get("t") || "";
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const q = `?t=${encodeURIComponent(t)}`;
  const load = useCallback(() => api.get(`/public/individual-managers/${id}${q}`).then((r) => setM(r.data)).catch((e) => setErr(formatApiError(e))), [id, q]);
  useEffect(() => { load(); }, [load]);
  const save = async (body, files, clear) => {
    setBusy(true);
    try { await api.put(`/public/individual-managers/${id}${q}`, body); await uploadAll(id, t, files); clear(); toast.success("Pendaftaran diperbarui"); await load(); window.scrollTo(0, 0); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const delFile = async (fid) => { try { await api.delete(`/public/individual-managers/${id}/files/${fid}${q}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  if (err) return <p className="max-w-2xl mx-auto px-4 py-16 text-red-700" data-testid="mgr-track-error">{err}</p>;
  if (!m) return <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const editable = ["BARU", "PERLU_PERBAIKAN"].includes(m.status);
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-5" data-testid="mgr-track-page">
      <div className="bg-white border rounded-xl p-5 space-y-2">
        <p className="text-sm text-slate-500">Pendaftaran Pengelola Perorangan</p>
        <h1 className="text-2xl font-extrabold text-slate-900">{m.number} · {m.fullName}</h1>
        <p className="text-sm">Status: <MgrStatusBadge s={m.status} /></p>
        {m.status === "PERLU_PERBAIKAN" && <p className="rounded-lg bg-orange-50 border border-orange-200 p-3 text-sm text-orange-900" data-testid="mgr-track-revision-note"><b>Perbaikan yang diminta PT SJI:</b> {m.revisionNote}</p>}
        {m.status === "DITOLAK" && <p className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900"><b>Alasan:</b> {m.rejectionNote}</p>}
        {m.status === "TERVERIFIKASI" && <p className="text-sm text-emerald-800">Selamat, Anda terverifikasi PT SJI. Masuk lewat halaman <Link to="/login" className="underline font-semibold">Masuk</Link> dengan email Anda.</p>}
        <p className="text-xs text-slate-500">{m.responsibility}</p>
      </div>
      <div className="bg-white border rounded-xl p-5"><h2 className="font-bold mb-2">File terunggah</h2>
        <ul className="space-y-1 text-sm" data-testid="mgr-track-files">{m.files.map((f) => <li key={f.id} className="flex justify-between gap-2">
          <a href={`${B}/api/public/individual-managers/${id}/files/${f.id}${q}`} target="_blank" rel="noreferrer" className="text-emerald-800 underline truncate"><FileText className="inline h-4 w-4 mr-1" />{FILE_KINDS[f.kind]}: {f.name}</a>
          {editable && <button type="button" onClick={() => delFile(f.id)} className="text-red-700" aria-label="Hapus file" data-testid={`mgr-track-file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>}</li>)}
          {!m.files.length && <li className="text-slate-500">Belum ada file.</li>}</ul>
      </div>
      {editable ? <ManagerForm key={m.updatedAt} initial={m} onSubmit={save} busy={busy} submitLabel={m.status === "PERLU_PERBAIKAN" ? "Kirim Perbaikan" : "Simpan Perubahan"} />
        : <p className="text-sm text-slate-600">Pendaftaran tidak dapat diubah pada status ini.</p>}
    </div>
  );
}
